<template>
  <v-container
    v-if="order"
  >
    <validation-observer ref="orderCarryInValidator">
      <v-card
        flat
      >
        <v-card-title></v-card-title>
        <v-card-text>
          <!-- 売上計上しない -->
          <v-row
            v-if="isBilling"
          >
            <v-col cols="12">
              <v-checkbox
                v-model="order.not_recorded_sales"
                dense
                label="売上計上しない"
                hide-details="auto"
              >
              </v-checkbox>
            </v-col>
          </v-row>
          <!-- /売上計上しない -->

          <!-- 営業担当者 -->
          <v-row>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                name="営業担当者"
                rules="max:12"
              >
                <v-combobox
                  v-model="order.sales_staff"
                  :error-messages="errors"
                  :items="salesStaffs"
                  :return-object="false"
                  dense
                  label="営業担当者"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>営業担当者</span>
                    <span class="ml-1">
                      ({{ order.sales_staff?.length || 0 }} / 12)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-combobox>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /営業担当者 -->

          <!-- 搬入業者 -->
          <v-row>
            <v-col cols="12" md="6">
              <infinite-trader-auto-complete
                :trader-id="order.transport_id"
                label="搬入業者"
                :required="true"
                :without-invisible="true"
                with-none-location
                @update:values="transportChangedCallback"
              >
                <template v-slot:append-outer>
                  <choose-trader-button
                    :small-button="true"
                    :search-type="`transport`"
                    :without-invisible="true"
                    :button-caption="`搬入業者を選択`"
                    with-none-location
                    @choseTraderCallback="choseTransportCallback"
                  />
                </template>
              </infinite-trader-auto-complete>
            </v-col>
          </v-row>
          <v-row v-if="selectedTransporter" dense>
            <v-col cols="12" md="6" class="mt-0 pt-0">
              <div class="ml-2">
                <div>
                  〒{{ selectedTransporter.zip_code }}&emsp;{{ selectedTransporter.address }}
                </div>
                <div>
                  電話番号:{{ selectedTransporter.phone_number || '-' }}
                </div>
              </div>
            </v-col>
          </v-row>
          <!-- /搬入業者 -->

          <!-- 運搬先事業者/運搬先事業場 -->
          <infinite-tenant-trader-and-location-auto-complete
            :trader-id="order.trader_id"
            :location-id="order.to_trader_location_id"
            :trader-required="true"
            :location-required="true"
            :without-invisible="true"
            trader-md="6"
            location-md="5"
            trader-label="運搬先事業者"
            location-label="運搬先事業場"
            location-auto-select
            search-type="either"
            @update:values="toTraderChangedCallback"
          >
            <template v-slot:trader-item="slotProps" >
              <v-chip
                :title="slotProps.item.subscriber_number"
                color="#e0ffff"
                drak
                label
                small
              >
                {{ slotProps.item.subscriber_number }}
              </v-chip>&nbsp;
              {{slotProps.item.text}}&nbsp;
              <small class="text--disabled">{{slotProps.item.address | truncate(20)}} </small>
            </template>
            <template v-slot:trader-append-outer>
              <choose-tenant-trader-button
                :small-button="true"
                :button-caption="`運搬先事業者を選択`"
                :without-invisible="true"
                @choseTraderCallback="choseTraderCallback"
              />
            </template>
            <template v-slot:location-item="slotProps">
              <v-chip
                :title="slotProps.item?.code"
                color="#e0ffff"
                label
                small
              >
                {{ slotProps.item?.code }}
              </v-chip>
              &nbsp;{{ slotProps.item.text }}
              <small class="text--disabled">{{ slotProps.item.address }}</small>
            </template>
          </infinite-tenant-trader-and-location-auto-complete>
          <v-row v-if="selectedTrader || selectedTraderLocation" dense>
            <v-col cols="12" md="6" class="mt-0 pt-0">
              <div v-if="selectedTrader" class="ml-2">
                <div>
                  〒{{ selectedTrader.zip_code }}&emsp;{{ selectedTrader.address }}
                </div>
                <div>
                  電話番号:{{ selectedTrader.phone_number || '-' }}
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="6" class="mt-0 pt-0">
              <div v-if="selectedTraderLocation" class="ml-2">
                <div>
                  〒{{ selectedTraderLocation.zip_code }}&emsp;{{ selectedTraderLocation.address }}
                </div>
                <div>
                  電話番号:{{ selectedTraderLocation.phone_number || '-' }}
                </div>
              </div>
            </v-col>
          </v-row>
          <!-- /運搬先事業者/運搬先事業場 -->

          <!-- 搬入予定日／予定時間 -->
          <v-row>
            <v-col cols="12" md="3">
              <cs-date-picker
                :date.sync="order.plan_date"
                @dateChangedCallback="planDateChangedCallback"
              >
                <template v-slot="{ on, attrs }">
                  <validation-provider
                    v-slot="{ errors }"
                    :rules="{required: true}"
                    name="搬入予定日"
                    class="requiredMark"
                  >
                    <v-text-field
                      v-model="order.plan_date"
                      v-bind="attrs"
                      v-on="on"
                      :error-messages="errors"
                      append-icon="mdi-calendar"
                      clearable
                      dense
                      hide-details="auto"
                      label="搬入予定日"
                      outlined
                      persistent-hint
                      persistent-placeholder
                      readonly
                    />
                  </validation-provider>
                </template>
              </cs-date-picker>
            </v-col>
            <v-col cols="12" md="2">
              <validation-provider
                v-slot="{ errors }"
                name="予定開始時間"
              >
                <vue-ctk-date-time-picker
                  v-model="order.plan_start_time"
                  minute-interval="15"
                  no-value-to-custom-elem
                  only-time
                  color="#0070B3"
                  format="HH:mm"
                  formatted="HH:mm"
                >
                  <v-text-field
                    v-model="order.plan_start_time"
                    :error-messages="errors"
                    append-icon="mdi-clock-time-four-outline"
                    clearable
                    dense
                    hide-details="auto"
                    label="予定開始時間"
                    outlined
                    persistent-hint
                    persistent-placeholder
                    placeholder="--:--"
                    readonly
                  />
                </vue-ctk-date-time-picker>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="2">
              <validation-provider
                v-slot="{ errors }"
                name="予定終了時間"
              >
                <vue-ctk-date-time-picker
                  v-model="order.plan_end_time"
                  minute-interval="15"
                  no-value-to-custom-elem
                  only-time
                  color="#0070B3"
                  format="HH:mm"
                  formatted="HH:mm"
                >
                  <v-text-field
                    v-model="order.plan_end_time"
                    :error-messages="errors"
                    append-icon="mdi-clock-time-four-outline"
                    clearable
                    dense
                    hide-details="auto"
                    label="予定終了時間"
                    outlined
                    persistent-hint
                    persistent-placeholder
                    placeholder="--:--"
                    readonly
                  />
                </vue-ctk-date-time-picker>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /搬入予定日／予定時間 -->

          <!-- 検索キーワード -->
          <v-row>
            <v-col cols="12" md="12">
              <validation-provider
                v-slot="{ errors }"
                name="検索キーワード"
                rules="max:25"
              >
                <v-text-field
                  v-model="order.keyword"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="検索キーワード"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>検索キーワード</span>
                    <span class="ml-1">
                      ({{ order.keyword?.length || 0 }} / 25)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /検索キーワード -->

          <!-- 期間終了日 -->
          <v-row v-if="order.repeat_setting !== 'unuse_repeat'">
            <v-col cols="12" md="3">
              <cs-date-picker
                :date.sync="order.period_end_date"
                :minimum-date="minimumPeriodEndDate"
                :maximum-date="maximumDate"
                @dateChangedCallback="periodEndDateChangedCallback"
              >
                <template v-slot="{ on, attrs }">
                  <validation-provider
                    v-slot="{ errors }"
                    :rules="{ required: true, comparePeriodDate: order.plan_date }"
                    name="期間終了日"
                    class="requiredMark"
                  >
                    <v-text-field
                      v-model="order.period_end_date"
                      v-bind="attrs"
                      v-on="on"
                      :error-messages="errors"
                      append-icon="mdi-calendar"
                      clearable
                      dense
                      hide-details="auto"
                      label="期間終了日"
                      outlined
                      persistent-hint
                      persistent-placeholder
                      readonly
                    />
                  </validation-provider>
                </template>
              </cs-date-picker>
            </v-col>
          </v-row>
          <!-- /期間終了日 -->
          <br />
          <v-divider />
          <v-card-actions
            v-if="isBilling && !order.not_recorded_sales"
            class="mt-3"
          >
            <validation-provider
              v-slot="{ errors }"
              class="requiredMark"
              name="受注情報"
              rules="required"
            >
              <v-input
                :value="filteredSalesItems.length > 0 ? 1: null"
                :error-messages="errors"
                dense
                label="受注情報"
                hide-details="auto"
                outlined
              />
            </validation-provider>
            <v-spacer />
            <v-btn
              class="primary"
              :disabled="!order.transport_id"
              @click="showSalesDialogButtonClickEvent"
            >
              追加
            </v-btn>
          </v-card-actions>
          <v-row v-if="isBilling && !order.not_recorded_sales">
            <v-col cols="12">
              <v-data-table
                :headers="salesTableHeaders"
                :items="filteredSalesItems"
                :items-per-page="-1"
                disable-pagination
                disable-sort
                hide-default-footer
              >
                <!-- 売上品目コード -->
                <template v-slot:[`item.sales_item_code`]="{item}">
                  <span v-if="item.amount_of_acceptance && item.amount_of_acceptance_unit_name">{{ item.sales_item_code }}</span>
                </template>
                <!-- /売上品目コード -->
                <!-- 受入量 -->
                <template v-slot:[`item.amount_of_acceptance`]="{item}">
                  <span v-if="item.amount_of_acceptance && item.amount_of_acceptance_unit_name">{{ parseFloat(item.amount_of_acceptance) | comma_format }}&nbsp;{{ item.amount_of_acceptance_unit_name }}</span>
                </template>
                <!-- /受入量 -->
                <template v-slot:[`item.editable`]="{ item }">
                  <v-btn
                    class="info mr-1"
                    small
                    @click="editButtonClickEvent(item)"
                  >
                    編集
                  </v-btn>
                  <v-btn
                    class="error"
                    small
                    @click="deleteButtonClickEvent(item)"
                  >
                    削除
                  </v-btn>
                </template>
              </v-data-table>
              <hr>
              <v-simple-table>
                <template v-slot:default>
                  <tbody>
                  <tr>
                    <th class="text-right" width="22.2%">受入量合計</th>
                    <td class="text-right" width="22.2%"></td>
                    <td class="text-right" width="22.2%">
                      <span v-if="totalAmountOfAcceptance > 0">{{ totalAmountOfAcceptance | comma_format }}&nbsp;{{ acceptanceUnitName }}</span>
                      <span v-else>{{ "ー" }}</span>
                    </td>
                    <td width="33.4%"></td>
                  </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>
          <v-divider />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="cancelButtonClickEvent"
          >
            閉じる
          </v-btn>
          <v-btn
            class="primary"
            @click="registerButtonClickEvent"
          >
            受注登録
          </v-btn>
        </v-card-actions>
      </v-card>
    </validation-observer>
    <sales-item-dialog
      :enable-dialog="enableSalesItemDialog"
      :sales-item-prices="salesItemPrices"
      :sales-item="selectedSalesItem"
      :sales-item-units="salesItemUnits"
      @cancelCallback="dialogCancelCallback"
      @registerCallback="dialogRegisterCallback"
    />
    <registration-provisional-dialog
      :enable-dialog="enableRegistrationProvisionalDialog"
      :enable-to-deal-button="!order.not_recorded_sales"
      :from-collection-request="false"
      :repeatSetting="'unuse_repeat'"
      @closeCallback="registrationProvisionalDialogCloseCallback"
      @registerCallback="registrationProvisionalRegisterCallback"
    />
    <confirm ref="confirm" />
  </v-container>
</template>

<script>
import SalesItemDialog from '../../components/_SalesItemDialog.vue'
import RegistrationProvisionalDialog from '../dialogs/_RegistrationProvisional.vue'
import ChooseTraderButton from "@/driver/components/ChooseTraderButton.vue"
import Confirm from '@/components/Confirm.vue'
import _ from 'lodash'
import { mapActions, mapState } from 'vuex'
import InfiniteTraderAutoComplete from "@/driver/components/InfiniteTraderAutoComplete.vue";
import InfiniteTenantTraderAndLocationAutoComplete
  from "@/driver/components/InfiniteTenantTraderAndLocationAutoComplete.vue";
import ChooseTenantTraderButton from "@/driver/components/ChooseTenantTraderButton.vue";

export default {
  name: 'OrderAcceptCarryInForm',
  components: {
    ChooseTenantTraderButton,
    InfiniteTenantTraderAndLocationAutoComplete,
    InfiniteTraderAutoComplete,
    SalesItemDialog,
    RegistrationProvisionalDialog,
    ChooseTraderButton,
    Confirm,
  },
  mixins: [],
  props: {},
  data() {
    return {
      enableSalesItemDialog: false,
      enableRegistrationProvisionalDialog: false,
      selectedSalesItem: null,
      order: null,
      /** 営業担当者情報 */
      salesStaffs: [],
      /** 受注情報 */
      salesTableHeaders: [
        { value: 'sales_item_code', text: '売上品目コード', align: 'end' , width: '22.2%'},
        { value: 'sales_item_name', text: '売上品目名' , width: '22.2%'},
        { value: 'amount_of_acceptance', text: '受入量', align: 'end' , width: '22.2%'},
        { value: 'editable', text: '', align: 'end' , width: '33.4%'},
      ],
      /* 選択された搬入業者 */
      selectedTransporter: null,
      selectedTrader: null,
      selectedTraderLocation: null,
      salesItemUnits: [],
      totalAmountOfAcceptance: 0,
      acceptanceUnitName: null,
      selectedTransportId: null,
      salesItemPrices: [],
      selectedToTraderIds: [],
    }
  },
  computed: {
    ...mapState('DriverTenantConfiguration', {
      isBilling: 'use_billing',
    }),
    /**
     * 期間終了日
     * 最小日付を算出
     */
    minimumPeriodEndDate () {
      if (this.order.plan_date) {
        return this.order.plan_date
      } else {
        return null
      }
    },
    /**
     * 期間終了日
     * 最大日付を算出
     */
    maximumDate () {
      if (this.order.plan_date) {
        return this.$dayjs(this.order.plan_date).add(3, 'months').format('YYYY-MM-DD')
      } else {
        return null
      }
    },
    /**
     * フィルターした受注情報リストを返す.
     * 削除ボタン押下により_destroyが設定された行は非表示とする.
     * @return {*[]}
     */
    filteredSalesItems () {
      let filteredList = []
      if (this.order) {
        if (this.order.order_carryin_details) {
          filteredList = this.order.order_carryin_details.filter(v => !v._destroy)
        }
      }
      return filteredList
    },
  },
  watch: {
    filteredSalesItems () {
      this.calculateTotalAmount()
    },
  },
  created() {
    this.fetchSalesStaffs().then((content) => {
      this.salesStaffs = content.sales_staffs
    })
    this.fetchSalesItemUnits().then((content) => {
      this.salesItemUnits = content.sales_item_units
    })

    if (this.$route.params.duplicate_id) {
      this.fetchDuplicateOrder({id: this.$route.params.duplicate_id}).then((content) => {
        this.order = content
        this.selectedTransporter = content.transport
        this.selectedTransportId = content.transport.id
        this.selectedTrader = content.trader
        this.selectedTraderLocation = content.trader.location
        // this.searchTraderLocationBy(content.trader_id)
        this.loadSalesItemPrices(content.transport.id)
        if (this.$route.query.plan_date) {
          this.order.plan_date = this.$route.query.plan_date;
        }
      })
    } else {
      this.fetchNew().then((content) => {
        this.order = content
        if (this.$route.query.plan_date) {
          this.order.plan_date = this.$route.query.plan_date;
        }
      })
    }

  },
  methods: {
    ...mapActions('DriverOrderAcceptCarryIn', {
      fetchNew: 'new',
      fetchDuplicateOrder: 'duplicate',
      createCarryIn: 'create',
    }),
    ...mapActions('DriverMasterSearchSupplierSalesItemPriceDetail', {
      fetchSalesItemPrices: 'index'
    }),
    ...mapActions('DriverMasterSearchSalesStaff', {
      fetchSalesStaffs: 'index',
    }),
    ...mapActions('DriverMasterSearchSalesItemUnit', {
      fetchSalesItemUnits: 'index',
    }),
    ...mapActions('snackbar', {
      showError: 'showError',
    }),
    ...mapActions("DriverMasterSearchTraderMember", {
      fetchTrader: "show"
    }),
    ...mapActions("DriverMasterSearchTraderLocationMember", {
      fetchTraderLocation: "show"
    }),
    /**
     * 搬入業者 チェンジコールバック
     * @param trader_id
     */
    transportChangedCallback (trader_id) {
      this.order.transport_id = trader_id;
      this.transportChangeEvent();
      this.loadTransport();
    },
    /**
     * 搬入業者選択モーダル コールバック
     * @param item
     */
    choseTransportCallback (item) {
      this.order.transport_id = item.id
      // this.transportChangeEvent();
      // this.loadTransport();
    },
    /**
     * 搬入業者情報の取得
     */
    loadTransport () {
      this.selectedTransporter = null;
      if (!this.order.transport_id) {
        return;
      }
      this.fetchTrader({ id: this.order.transport_id }).then((content) => {
        this.selectedTransporter = content;
      })

    },
    /**
     * 運搬先事業者 チェンジコールバック
     * @param trader_id { Number } 事業者ID
     * @param location_id { Number } 事業場ID
     * @param location_code { String } 事業場コード
     */
    toTraderChangedCallback (trader_id, location_id, location_code) {
      this.order.trader_id = trader_id;
      this.order.to_trader_location_id = location_id;
      this.order.to_trader_location_code = location_code;
      this.loadTrader();
      this.loadToTraderLocation();
    },
    choseTraderCallback (item) {
      this.order.trader_id = item.id;
      this.order.to_trader_location_id = null;
      this.order.to_trader_location_code = null;
    },

    loadTrader () {
      this.selectedTrader = null;
      if (!this.order.trader_id) {
        return;
      }
      this.fetchTrader({ id: this.order.trader_id }).then((content) => {
        this.selectedTrader = content;
      })
    },
    loadToTraderLocation () {
      this.selectedTraderLocation = null;
      if (!this.order.to_trader_location_id) {
        return
      }
      this.fetchTraderLocation({ id: this.order.to_trader_location_id }).then((content) => {
        this.selectedTraderLocation = content;
      })

    },

    /**
     * 搬入業者 チェンジイベント
     */
    transportChangeEvent () {
      if (this.order.transport_id) {

        if ((this.selectedTransportId && this.order.transport_id !== this.selectedTransportId) && this.filteredSalesItems.length > 0) {

          let modalOption = { title: '確認', message: '搬入業者が変更されたので受注情報をクリアします。よろしいですか？', options: { width: 380, isCancelButton: true } }
          this.$refs.confirm.open(modalOption).then((confirm) => {
            this.$refs.confirm.close()
            if (confirm) {
              for (let i = 0; i < this.order.order_carryin_details.length; i++) {
                this.order.order_carryin_details[i]._destroy = true
              }
              this.loadSalesItemPrices(this.order.transport_id)
              this.selectedTransportId = this.order.transport_id
            } else {
              this.order.transport_id = this.selectedTransportId
            }
          })
        } else {
          this.selectedTransportId = this.order.transport_id
          this.loadSalesItemPrices(this.order.transport_id)
        }
      }
    },
    showSalesDialogButtonClickEvent () {
      this.selectedSalesItem = null
      this.enableSalesItemDialog = true
    },
    dialogCancelCallback () {
      this.selectedSalesItem = null
      this.enableSalesItemDialog = false
    },
    dialogRegisterCallback (item) {
      let detail = this.order.order_carryin_details.find(v => v.key === item.key)
      if (detail) {
        Object.assign(detail, item)
      } else {
        this.order.order_carryin_details.push(item)
      }
      this.calculateTotalAmount()
      this.dialogCancelCallback()
    },
    /**
     * 搬入予定日変更　コールバック
     */
    planDateChangedCallback (value) {
      this.order.plan_date = value;
    },
    /**
     * 期間終了日変更　コールバック
     */
    periodEndDateChangedCallback (value) {
      this.order.period_end_date = value;
    },
    /**
     * 受注情報編集ボタン クリックイベント
     * @param item
     */
    editButtonClickEvent (item) {
      this.selectedSalesItem = _.cloneDeep(item)
    },
    /**
     * 受注情報削除ボタン クリックイベント
     * @param item
     */
    deleteButtonClickEvent (item) {
      let modalOption = { title: '削除', message: '削除しますがよろしいですか？', options: { width: 320, titleColor: 'red', isCancelButton: true } }
        this.$refs.confirm.open(modalOption).then((confirm) => {
        this.$refs.confirm.close()
        if (confirm) {
          for (const sales_item of this.order.order_carryin_details) {
            if (sales_item.key === item.key) {
              sales_item._destroy = true
            }
          }
          this.calculateTotalAmount()
        }
      })
    },
    /**
     * キャンセルボタン クリックイベント
     */
    cancelButtonClickEvent () {
      this.$router.push( { name: 'DriverOrderAccepts' } )
    },
    /**
    * 受注登録ボタン クリックイベント
    */
    registerButtonClickEvent () {
      return new Promise((resolve) => {
        setTimeout(() => {
          this.$refs.orderCarryInValidator.validateWithInfo().then(({ isValid, errors }) => {
            if (isValid) {
              this.enableRegistrationProvisionalDialog = true
            } else {
              let messages = []
              for (let key in errors) {
                if (errors[key].length > 0) {
                  messages.push(errors[key][0])
                }
              }
              this.showError({ message: messages.join(',') })
            }
          })
          resolve();
        }, 1000);
      });
    },
    /**
     * 登録モーダルからの戻り
     * @param afterTransaction { Boolean }　true: 売上入力へ, false: 一覧へ
     */
    registrationProvisionalRegisterCallback (afterTransaction) {
      const params = this.buildParams()
      this.createCarryIn({ params: params }).then((content) => {
        if (afterTransaction) { // OKして売上入力へ進む
          this.$router.push({name: 'DriverSalesTransactionEdit', params: { id: content.deal.id }})
        } else { // OKして閉じる(一覧画面に戻る)
          this.$router.push({ name: 'DriverOrderAccepts' })
        }
      })
    },
    registrationProvisionalDialogCloseCallback () {
      this.enableRegistrationProvisionalDialog = false
    },
    loadSalesItemPrices (transport_id) {
      if (transport_id) {
        this.fetchSalesItemPrices({id: transport_id, type: 'Trader', only_jwnet_item: true}).then((content) =>{
          this.salesItemPrices = content.sales_item_price_details
        })
      }
    },
    /**
     * 受入量合計の算出
     */
    calculateTotalAmount () {
      this.totalAmountOfAcceptance = 0
      if (this.order && this.order.order_carryin_details) {
        // 受入量単位あり かつ 表示中のリスト
        let isAcceptanceUnitList = this.order.order_carryin_details.filter(v => v.amount_of_acceptance_unit_name !== null && !v._destroy)
        if (isAcceptanceUnitList.length > 0) {

          let acceptanceUnitAllEqual = isAcceptanceUnitList.every(v => v.amount_of_acceptance_unit_name === isAcceptanceUnitList[0].amount_of_acceptance_unit_name)
          if (acceptanceUnitAllEqual) {
            // 受入量合計 単位
            this.acceptanceUnitName = isAcceptanceUnitList[0].amount_of_acceptance_unit_name
            // 受入量合計
            for (const sales_item of isAcceptanceUnitList) {
              this.totalAmountOfAcceptance += parseFloat(sales_item.amount_of_acceptance)
            }
          }
        }
      }
    },
    buildParams () {
      const params = new FormData()
      /* 売上計上しない */
      params.append('not_recorded_sales', this.nullToBlank(this.order.not_recorded_sales))
      /* 営業担当者 */
      params.append('sales_staff', this.nullToBlank(this.order.sales_staff))
      /* 検索キーワード */
      params.append('keyword', this.nullToBlank(this.order.keyword))
      /* 搬入予定日（orders） */
      params.append('plan_date', this.nullToBlank(this.order.plan_date))
      /* 予定開始時間（orders） */
      params.append('plan_start_time', this.nullToBlank(this.order.plan_start_time))
      /* 予定終了時間（orders） */
      params.append('plan_end_time', this.nullToBlank(this.order.plan_end_time))
      /* 搬入業者ID */
      params.append('order_carryin_attributes[transport_id]', this.nullToBlank(this.order.transport_id))
      /* 運搬先事業者ID */
      params.append('order_carryin_attributes[trader_id]', this.nullToBlank(this.order.trader_id))
      /* 運搬先事業場コード */
      params.append('order_carryin_attributes[to_trader_location_code]', this.nullToBlank(this.order.to_trader_location_code))
      /* 搬入予定日 */
      params.append('order_carryin_attributes[carryin_plan_date]', this.nullToBlank(this.order.plan_date))
      /* 予定開始時間（order_carryins） */
      params.append('order_carryin_attributes[carryin_plan_time]', this.nullToBlank(this.order.plan_start_time))
      /* 予定終了時間（order_carryins） */
      params.append('order_carryin_attributes[carryin_plan_end_time]', this.nullToBlank(this.order.plan_end_time))

      if (this.isBilling && !this.order.not_recorded_sales) {
        this.order.order_carryin_details.forEach((order_carryin_detail, index) => {
          /* 売上品目コード */
          params.append(`order_carryin_attributes[order_carryin_details_attributes][${index}][sales_item_price_code]`, this.nullToBlank(order_carryin_detail.sales_item_code))
          /* 売上品目名 */
          params.append(`order_carryin_attributes[order_carryin_details_attributes][${index}][sales_item_price_name]`, this.nullToBlank(order_carryin_detail.sales_item_name))
          /* 受入量 */
          params.append(`order_carryin_attributes[order_carryin_details_attributes][${index}][received_quantity]`, this.nullToBlank(order_carryin_detail.amount_of_acceptance))
          /* 受入量単位 */
          params.append(`order_carryin_attributes[order_carryin_details_attributes][${index}][sales_item_unit_id]`, this.nullToBlank(order_carryin_detail.amount_of_acceptance_unit_id))
          /* 削除 */
          params.append(`order_carryin_attributes[order_carryin_details_attributes][${index}][_destroy]`, this.nullToBlank(order_carryin_detail._destroy))
        })
      }
      return params
    },

  }
}
</script>

<style scoped lang="scss">

</style>
